import React, { useState, useEffect } from 'react';
import { tournamentsService, matchesService, teamsService, playersService } from '../../services/supabaseService';
import { sendTournamentResultEmail, sendMatchResultEmail } from '../../services/emailService';
import { notifySlackResultsPublished } from '../../services/slackService';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import TournamentSelector from './components/TournamentSelector';
import MatchFilterBar from './components/MatchFilterBar';
import MatchResultCard from './components/MatchResultCard';
import PredictionImpactPanel from './components/PredictionImpactPanel';
import BulkResultEntry from './components/BulkResultEntry';
import CalculationStatus from './components/CalculationStatus';
import ExportReports from './components/ExportReports';
import ConfirmationDialog from './components/ConfirmationDialog';
import Icon from '../../components/AppIcon';
import TournamentResultCard from './components/TournamentResultCard';
import Toast from '../../components/ui/Toast';

const ResultsManagement = () => {
  const [selectedTournament, setSelectedTournament] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    format: 'all',
    stage: 'all'
  });
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [impactData, setImpactData] = useState(null);
  const [calculationStatus, setCalculationStatus] = useState({ state: 'idle' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    type: 'default',
    onConfirm: () => {}
  });
  const [tournaments, setTournaments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishedSectionOpen, setPublishedSectionOpen] = useState(false);
  const [pendingSectionOpen, setPendingSectionOpen] = useState(true);


  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    if (selectedTournament) {
      fetchMatches();
      fetchTeamsAndPlayers();
    }
  }, [selectedTournament]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const tournamentsData = await tournamentsService?.getAll();
      
      const transformedTournaments = tournamentsData?.map(tournament => ({
        id: tournament?.id,
        name: tournament?.name,
        format: tournament?.format,
        status: tournament?.status,
        startDate: tournament?.startDate,
        endDate: tournament?.endDate,
        tournamentResultsStatus: tournament?.tournamentResultsStatus,
        championTeamId: tournament?.championTeamId,
        finalist1Id: tournament?.finalist1Id,
        finalist2Id: tournament?.finalist2Id,
        semifinalist1Id: tournament?.semifinalist1Id,
        semifinalist2Id: tournament?.semifinalist2Id,
        semifinalist3Id: tournament?.semifinalist3Id,
        semifinalist4Id: tournament?.semifinalist4Id,
        playerOfTournamentId: tournament?.playerOfTournamentId,
        top4Team1Id: tournament?.top4Team1Id,
        top4Team2Id: tournament?.top4Team2Id,
        top4Team3Id: tournament?.top4Team3Id,
        top4Team4Id: tournament?.top4Team4Id,
        superOverTeamIds: Array.isArray(tournament?.superOverTeamIds) ? tournament?.superOverTeamIds : (tournament?.superOverTeamIds ? [tournament?.superOverTeamIds] : [])
      })) || [];

      setTournaments(transformedTournaments);
      
      if (transformedTournaments?.length > 0) {
        setSelectedTournament(transformedTournaments?.[0]?.id);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const matchesData = await matchesService?.getByTournament(selectedTournament);
      
      const transformedMatches = matchesData?.map(match => ({
        id: match?.id,
        tournamentId: match?.tournamentId,
        matchNumber: match?.matchNumber,
        team1Id: match?.team1?.id,
        team1: match?.team1?.name,
        team1ShortName: match?.team1?.shortName || match?.team1?.name?.substring(0, 3)?.toUpperCase(),
        team1Logo: match?.team1?.logo,
        team1LogoAlt: match?.team1?.logoAlt,
        team2Id: match?.team2?.id,
        team2: match?.team2?.name,
        team2ShortName: match?.team2?.shortName || match?.team2?.name?.substring(0, 3)?.toUpperCase(),
        team2Logo: match?.team2?.logo,
        team2LogoAlt: match?.team2?.logoAlt,
        date: match?.matchDate,
        venue: match?.venue,
        format: tournaments?.find(t => t?.id === selectedTournament)?.format,
        stage: match?.stage,
        status: match?.status,
        team1Score: match?.team1Score || '',
        team1Wickets: match?.team1Wickets || '',
        team2Score: match?.team2Score || '',
        team2Wickets: match?.team2Wickets || '',
        winnerId: match?.winnerId || '',
        tossWinnerId: match?.tossWinnerId || '',
        battingFirstId: match?.battingFirstId || '',
        manOfMatchId: match?.manOfMatchId || '',
        highestScorerId: match?.highestScorerId || '',
        highestWicketTakerId: match?.highestWicketTakerId || '',
        resultsStatus: match?.resultsStatus || ''
      })) || [];

      setMatches(transformedMatches);
    } catch (err) {
      console.error('Error fetching matches:', err);
    }
  };

  const fetchTeamsAndPlayers = async () => {
    try {
      // Fetch teams for the selected tournament
      const teamsData = await teamsService?.getByTournament(selectedTournament);
      const transformedTeams = teamsData?.map(item => ({
        id: item?.teams?.id,
        name: item?.teams?.name,
        shortName: item?.teams?.shortName || item?.teams?.name?.substring(0, 3)?.toUpperCase(),
        logo: item?.teams?.logo,
        logoAlt: item?.teams?.logoAlt
      })) || [];
      setTeams(transformedTeams);

      // Fetch players for the selected tournament
      const playersData = await playersService?.getByTournament(selectedTournament);
      const transformedPlayers = playersData?.map(item => ({
        id: item?.id,
        name: item?.name,
        teamId: item?.teamId,
        role: item?.role
      })) || [];
      
      // Add team short name to each player for display
      const playersWithTeam = transformedPlayers?.map(player => {
        const team = transformedTeams?.find(t => t?.id === player?.teamId);
        return {
          ...player,
          team: team?.shortName || team?.name || 'Unknown Team'
        };
      });
      
      setPlayers(playersWithTeam);
    } catch (err) {
      console.error('Error fetching teams and players:', err);
    }
  };

  const handleUpdateResult = async (matchId, resultData) => {
    try {
      await matchesService?.updateResult(matchId, resultData);
      await fetchMatches();
      setToast({ show: true, message: 'Match result updated successfully!', type: 'success' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error updating match result:', err);
      setToast({ show: true, message: 'Failed to update match result. Please try again.', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      format: 'all',
      stage: 'all'
    });
  };

  const getFilteredMatches = () => {
    return matches?.filter((match) => {
      if (match?.tournamentId !== selectedTournament) return false;
      if (filters?.status !== 'all' && match?.status !== filters?.status) return false;
      if (filters?.format !== 'all' && match?.format !== filters?.format) return false;
      if (filters?.stage !== 'all' && match?.stage !== filters?.stage) return false;
      return true;
    });
  };

  const handleSaveDraft = async (matchId, resultData) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Save as Draft',
      message: 'This will save the result data without publishing. You can complete and publish it later.',
      confirmText: 'Save Draft',
      type: 'default',
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          setCalculationStatus({
            state: 'processing',
            currentMatch: 0,
            totalMatches: 1
          });

          // Strip matchStatus (not a DB column) before saving
          const { matchStatus, ...cleanResultData } = resultData;

          // Save draft to database with resultsStatus 'draft'
          await matchesService?.updateResult(matchId, {
            ...cleanResultData,
            resultsStatus: 'draft'
          });

          // Refresh matches to show updated data
          await fetchMatches();

          setCalculationStatus({
            state: 'success',
            totalMatches: 1,
            affectedUsers: 0
          });

          // Show success toast
          setToast({
            show: true,
            message: 'Draft saved successfully!',
            type: 'success'
          });

          setTimeout(() => {
            setCalculationStatus({ state: 'idle' });
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        } catch (err) {
          console.error('Error saving draft:', err);
          setCalculationStatus({ state: 'idle' });
          setToast({
            show: true,
            message: 'Failed to save draft. Please try again.',
            type: 'error'
          });
          setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        }
      }
    });
  };

  const handlePublishResult = (matchId, resultData, isEdit = false) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Publish Result',
      message: `This will publish the result and automatically calculate points for all. This action cannot be undone.`,
      confirmText: 'Publish & Calculate',
      type: 'warning',
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          setCalculationStatus({
            state: 'processing',
            currentMatch: 0,
            totalMatches: 1
          });

          // Strip matchStatus (not a DB column) and derive correct status
          const { matchStatus, ...cleanResultData } = resultData;
          const matchDbStatus = matchStatus === 'cancelled' ? 'cancelled' : 'completed';

          // Save match result with published status
          await matchesService?.updateResult(matchId, {
            ...cleanResultData,
            resultsStatus: 'published',
            status: matchDbStatus
          });

          const calculationResult = await tournamentsService?.calculateMatchPoints(matchId);

          setImpactData({
            affectedUsers: calculationResult?.affectedUsers || 0,
            totalPredictions: calculationResult?.affectedUsers || 0,
            correctPredictions: 0,
            leaderboardChanges: calculationResult?.affectedUsers || 0
          });

          const match = matches?.find(m => m?.id === matchId);
          const tournament = tournaments?.find(t => t?.id === selectedTournament);

          // Send result notification emails to all tournament participants
          if (!isEdit && tournament && match) {
            sendMatchResultEmail(
              selectedTournament,
              tournament?.name,
              match?.team1,
              match?.team2,
              match?.matchNumber
            )?.catch(err => console.error('Email sending error:', err));
          }

          // Refresh matches to show updated data
          await fetchMatches();

          setCalculationStatus({
            state: 'success',
            totalMatches: 1,
            affectedUsers: calculationResult?.affectedUsers || 0
          });

          // Show success toast
          setToast({
            show: true,
            message: 'Result published and points calculated successfully!',
            type: 'success'
          });

          // Notify Slack about match result published
          if (!isEdit) {
            try {
              const slackMatch = matches?.find(m => m?.id === matchId);
              const slackTournament = tournaments?.find(t => t?.id === selectedTournament);
              await notifySlackResultsPublished({
                tournamentId: selectedTournament,
                tournamentName: slackTournament?.name || 'Tournament',
                message: `Match result published: ${slackMatch?.team1 || ''} vs ${slackMatch?.team2 || ''}`,
              });
            } catch (slackErr) {
              console.error('Slack notification failed (match result):', slackErr);
            }
          }

          setTimeout(() => {
            setCalculationStatus({ state: 'idle' });
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        } catch (err) {
          console.error('Error publishing result:', err);
          setCalculationStatus({ state: 'idle' });
          setToast({
            show: true,
            message: 'Failed to publish result. Please try again.',
            type: 'error'
          });
          setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        }
      }
    });
  };

  const handlePublishTournamentResult = (tournamentId, resultData, action = 'publish') => {
    const isEdit = action === 'edit';
    setConfirmDialog({
      isOpen: true,
      title: isEdit ? 'Edit Tournament Results' : 'Publish Tournament Results',
      message: isEdit 
        ? 'This will update the tournament results and recalculate points for all users.'
        : 'This will publish the tournament results and calculate points for all users. This action cannot be undone.',
      confirmText: isEdit ? 'Update & Recalculate' : 'Publish & Calculate',
      type: 'warning',
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          setCalculationStatus({
            state: 'processing',
            currentMatch: 0,
            totalMatches: 1
          });

          // Update tournament results
          await tournamentsService?.updateTournamentResults(tournamentId, {
            ...resultData,
            tournamentResultsStatus: 'published'
          });

          // Calculate points for all users
          const calculationResult = await tournamentsService?.calculateTournamentPoints(tournamentId);

          // Send result notification emails to all tournament participants
          const tournament = tournaments?.find(t => t?.id === tournamentId);
          if (!isEdit && tournament) {
            sendTournamentResultEmail(tournamentId, tournament?.name)?.catch(err => console.error('Email sending error:', err));
          }

          // Refresh data
          await fetchTournaments();

          setCalculationStatus({
            state: 'success',
            totalMatches: 1,
            affectedUsers: calculationResult?.affectedUsers || 0
          });

          // Show success toast
          setToast({
            show: true,
            message: isEdit 
              ? 'Tournament results updated and points recalculated successfully!' :'Tournament results published and points calculated successfully!',
            type: 'success'
          });

          // Notify Slack about tournament results published
          if (!isEdit) {
            try {
              const tournament = tournaments?.find(t => t?.id === tournamentId);
              await notifySlackResultsPublished({
                tournamentId,
                tournamentName: tournament?.name || 'Tournament',
                message: `Tournament results are now live! Check the leaderboard to see the final standings.`,
              });
            } catch (slackErr) {
              console.error('Slack notification failed (tournament result):', slackErr);
            }
          }

          setTimeout(() => {
            setCalculationStatus({ state: 'idle' });
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        } catch (err) {
          console.error('Error publishing tournament results:', err);
          setCalculationStatus({ state: 'idle' });
          setToast({
            show: true,
            message: 'Failed to publish tournament results. Please try again.',
            type: 'error'
          });
          setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        }
      }
    });
  };

  const handleSavePartialTournamentResult = (tournamentId, partialData) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Save Tournament Results',
      message: 'This will save the filled result fields and calculate points for those categories. You can continue filling remaining fields and publish later.',
      confirmText: 'Save & Calculate Points',
      type: 'default',
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          setCalculationStatus({
            state: 'processing',
            currentMatch: 0,
            totalMatches: 1
          });

          // Merge partial data with existing tournament data so we don't overwrite already-saved fields
          const existingTournament = tournaments?.find(t => t?.id === tournamentId);
          const mergedData = {
            championTeamId: partialData?.championTeamId || existingTournament?.championTeamId || null,
            finalist1Id: partialData?.finalist1Id || existingTournament?.finalist1Id || null,
            finalist2Id: partialData?.finalist2Id || existingTournament?.finalist2Id || null,
            semifinalist1Id: partialData?.semifinalist1Id || existingTournament?.semifinalist1Id || null,
            semifinalist2Id: partialData?.semifinalist2Id || existingTournament?.semifinalist2Id || null,
            semifinalist3Id: partialData?.semifinalist3Id || existingTournament?.semifinalist3Id || null,
            semifinalist4Id: partialData?.semifinalist4Id || existingTournament?.semifinalist4Id || null,
            playerOfTournamentId: partialData?.playerOfTournamentId || existingTournament?.playerOfTournamentId || null,
            top4Team1Id: partialData?.top4Team1Id || existingTournament?.top4Team1Id || null,
            top4Team2Id: partialData?.top4Team2Id || existingTournament?.top4Team2Id || null,
            top4Team3Id: partialData?.top4Team3Id || existingTournament?.top4Team3Id || null,
            top4Team4Id: partialData?.top4Team4Id || existingTournament?.top4Team4Id || null,
            superOverTeamIds: partialData?.superOverTeamIds?.length > 0
              ? partialData?.superOverTeamIds
              : (existingTournament?.superOverTeamIds || []),
            tournamentResultsStatus: 'partial'
          };

          await tournamentsService?.updateTournamentResults(tournamentId, mergedData);

          // Calculate points for the saved fields
          const calculationResult = await tournamentsService?.calculateTournamentPoints(tournamentId);

          await fetchTournaments();

          setCalculationStatus({
            state: 'success',
            totalMatches: 1,
            affectedUsers: calculationResult?.affectedUsers || 0
          });

          setToast({
            show: true,
            message: 'Results saved and points calculated for filled fields!',
            type: 'success'
          });

          setTimeout(() => {
            setCalculationStatus({ state: 'idle' });
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        } catch (err) {
          console.error('Error saving partial tournament results:', err);
          setCalculationStatus({ state: 'idle' });
          setToast({
            show: true,
            message: 'Failed to save results. Please try again.',
            type: 'error'
          });
          setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
          }, 3000);
        }
      }
    });
  };

  const handleBulkSubmit = async (selectedMatches) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Process Bulk Results',
      message: `This will process ${selectedMatches?.length} match results and calculate points for all affected users. This may take several minutes.`,
      confirmText: 'Process All',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        setCalculationStatus({
          state: 'processing',
          currentMatch: 0,
          totalMatches: selectedMatches?.length
        });

        let totalAffected = 0;
        try {
          for (let i = 0; i < selectedMatches?.length; i++) {
            const matchId = selectedMatches?.[i];
            const matchData = matches?.find(m => m?.id === matchId);

            if (matchData) {
              await matchesService?.updateResult(matchId, {
                ...matchData,
                resultsStatus: 'published',
                status: 'completed'
              });
              const result = await tournamentsService?.calculateMatchPoints(matchId);
              totalAffected += result?.affectedUsers || 0;
            }

            setCalculationStatus((prev) => ({
              ...prev,
              currentMatch: i + 1
            }));
          }

          await fetchMatches();

          setCalculationStatus({
            state: 'success',
            totalMatches: selectedMatches?.length,
            affectedUsers: totalAffected
          });
          setToast({ show: true, message: `Bulk results processed for ${selectedMatches?.length} matches.`, type: 'success' });
          setTimeout(() => {
            setCalculationStatus({ state: 'idle' });
            setToast({ show: false, message: '', type: 'success' });
          }, 5000);
        } catch (err) {
          console.error('Error processing bulk results:', err);
          setCalculationStatus({ state: 'idle' });
          setToast({ show: true, message: 'Error processing bulk results. Please try again.', type: 'error' });
          setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
        }
      }
    });
  };

  const handleExport = async (tournamentId, reportType) => {
    try {
      const tournament = tournaments?.find(t => t?.id === tournamentId);
      const tournamentName = tournament?.name || 'tournament';
      let csvContent = '';
      let filename = '';

      if (reportType === 'leaderboard') {
        const leaderboardData = await tournamentsService?.getLeaderboard(tournamentId);
        csvContent = 'Rank,User,Total Points,Tournament Points,Match Points,Confidence Bonus\n';
        leaderboardData?.forEach((entry, idx) => {
          csvContent += `${idx + 1},"${entry?.userName || entry?.userId}",${entry?.totalPoints || 0},${entry?.tournamentPredictionPoints || 0},${entry?.matchPredictionPoints || 0},${entry?.confidenceBonus || 0}\n`;
        });
        filename = `${tournamentName}-leaderboard.csv`;
      } else if (reportType === 'results') {
        const tournamentMatches = matches?.filter(m => m?.tournamentId === tournamentId && m?.status === 'completed');
        csvContent = 'Match,Team 1,Team 2,Winner,Man of Match,Status\n';
        tournamentMatches?.forEach(m => {
          const winnerName = m?.winnerId === m?.team1?.id ? m?.team1?.name : m?.winnerId === m?.team2?.id ? m?.team2?.name : 'No Result';
          csvContent += `"Match ${m?.matchNumber}","${m?.team1?.name || ''}","${m?.team2?.name || ''}","${winnerName}","${m?.manOfMatchName || ''}","${m?.status}"\n`;
        });
        filename = `${tournamentName}-results.csv`;
      } else {
        csvContent = 'Report type not supported yet\n';
        filename = `${tournamentName}-report.csv`;
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link?.click();
      URL.revokeObjectURL(link?.href);

      setToast({ show: true, message: `${reportType} report exported successfully!`, type: 'success' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Export error:', err);
      setToast({ show: true, message: 'Failed to export report. Please try again.', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    }
  };

  const filteredMatches = getFilteredMatches();
  const pendingCount = matches?.filter((m) => m?.tournamentId === selectedTournament && m?.status === 'pending')?.length;

  const publishedMatches = filteredMatches?.filter(
    (m) => m?.resultsStatus === 'published' || m?.status === 'cancelled'
  );
  const pendingMatches = filteredMatches?.filter(
    (m) => m?.resultsStatus !== 'published' && m?.status !== 'cancelled'
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Results Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Post match results and manage automated points calculation
            </p>
          </div>

          <div className="space-y-6">
            <TournamentSelector
              tournaments={tournaments}
              selectedTournament={selectedTournament}
              onTournamentChange={setSelectedTournament}
              pendingCount={pendingCount} />


            <MatchFilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              matchCount={filteredMatches?.length} />


            <CalculationStatus status={calculationStatus} />

            {/* Tournament Results Section */}
            {selectedTournament && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Icon name="Trophy" size={20} className="text-warning" />
                  <h2 className="text-xl font-semibold text-foreground">Tournament Results</h2>
                </div>
                <TournamentResultCard
                  tournament={tournaments?.find(t => t?.id === selectedTournament)}
                  onPublishResult={handlePublishTournamentResult}
                  onSavePartial={handleSavePartialTournamentResult}
                  teams={teams}
                  players={players}
                />
              </div>
            )}

            {/* Match Results Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={20} className="text-primary" />
                <h2 className="text-xl font-semibold text-foreground">Match Results</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  {filteredMatches?.length === 0 ? (
                    <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
                      <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No Matches Found
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Try adjusting your filters to see more results
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Pending Section */}
                      <div className="bg-card rounded-lg border border-border overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between px-4 md:px-6 py-4 hover:bg-muted/50 transition-colors"
                          onClick={() => setPendingSectionOpen(prev => !prev)}
                        >
                          <div className="flex items-center gap-2">
                            <Icon name="Clock" size={18} className="text-warning" />
                            <span className="font-semibold text-foreground">Pending</span>
                            <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning rounded-full">
                              {pendingMatches?.length}
                            </span>
                          </div>
                          <Icon name={pendingSectionOpen ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground" />
                        </button>
                        {pendingSectionOpen && (
                          <div className="border-t border-border divide-y divide-border">
                            {pendingMatches?.length === 0 ? (
                              <div className="p-6 text-center text-sm text-muted-foreground">
                                No pending matches
                              </div>
                            ) : (
                              pendingMatches?.map((match, index) => (
                                <div key={match?.id} className="p-4 md:p-6">
                                  <MatchResultCard
                                    match={match}
                                    onSaveDraft={handleSaveDraft}
                                    onPublishResult={handlePublishResult}
                                    teams={teams}
                                    players={players}
                                    defaultExpanded={index === 0}
                                  />
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>

                      {/* Published Section */}
                      <div className="bg-card rounded-lg border border-border overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between px-4 md:px-6 py-4 hover:bg-muted/50 transition-colors"
                          onClick={() => setPublishedSectionOpen(prev => !prev)}
                        >
                          <div className="flex items-center gap-2">
                            <Icon name="CheckCircle" size={18} className="text-success" />
                            <span className="font-semibold text-foreground">Published</span>
                            <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-success/10 text-success rounded-full">
                              {publishedMatches?.length}
                            </span>
                          </div>
                          <Icon name={publishedSectionOpen ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground" />
                        </button>
                        {publishedSectionOpen && (
                          <div className="border-t border-border divide-y divide-border">
                            {publishedMatches?.length === 0 ? (
                              <div className="p-6 text-center text-sm text-muted-foreground">
                                No published matches
                              </div>
                            ) : (
                              publishedMatches?.map((match) => (
                                <div key={match?.id} className="p-4 md:p-6">
                                  <MatchResultCard
                                    match={match}
                                    onSaveDraft={handleSaveDraft}
                                    onPublishResult={handlePublishResult}
                                    teams={teams}
                                    players={players}
                                  />
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-6">
                  <PredictionImpactPanel impactData={impactData} />
                  
                  <BulkResultEntry
                    matches={matches?.filter((m) => m?.tournamentId === selectedTournament)}
                    onBulkSubmit={handleBulkSubmit} />


                  <ExportReports
                    tournamentId={selectedTournament}
                    onExport={handleExport} />

                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ConfirmationDialog
        isOpen={confirmDialog?.isOpen}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog?.onConfirm}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmText={confirmDialog?.confirmText}
        type={confirmDialog?.type} />

      {toast?.show && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>);

};

export default ResultsManagement;